// The cortex web app as a host of @cortex/sdk-guides-react (ADR 0060): this file only WIRES the SDK's views and tour
// overlay to cortex — the SDK's client for the routed project branch (the api's origin and the session's bearer,
// under where apps/api mounts the guides), the router (query string, navigation, the app links, a tour step's
// `{slug}`/`{branch}` placeholders), the Board's "Build a …" dialog, and cortex's own product tour in the chooser (not
// in a project's standalone shell, which has none of cortex's UI).
import { createGuidesClient } from '@cortex/sdk-guides-client';
import {
  GuidesHostProvider,
  GuidesPage as SdkGuidesPage,
  TourHost,
  type BuildRequestButtonProps,
  type BuiltInTours,
  type GuidesHost,
} from '@cortex/sdk-guides-react';
import * as React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { apiBaseUrl, apiFetch, getAccessToken } from './api.ts';
import { DEFAULT_BRANCH, projectPath } from './nav/paths.ts';
import { BuildRequestButton } from './board.tsx';
import { START_TUTORIAL_EVENT } from './events.ts';
import { coreTour } from './tour/tours.ts';
import { OwnViews } from './copy-views.tsx';

/** Where cortex mounts the guides SDK for a project branch (apps/api's mountSdkPackage). */
type Where = { slug: string; branch: string };

/** The SDK's client for one preview: the api's origin and bearer, under the project branch's prefix. */
export const guidesClientFor = (where: Where) =>
  createGuidesClient({
    baseUrl: `${apiBaseUrl}/api/guides/projects/${encodeURIComponent(where.slug)}/branches/${encodeURIComponent(where.branch)}`,
    token: () => getAccessToken() ?? null,
    fetch: apiFetch,
  });

/** A tour step's path as a guide wrote it, with the project branch filled in (`{line}`, or `{branch}` as before). */
export const fillProjectPath = (path: string, where: Where) =>
  path
    .replaceAll('{slug}', encodeURIComponent(where.slug))
    .replaceAll('{line}', encodeURIComponent(where.branch))
    .replaceAll('{branch}', encodeURIComponent(where.branch));

const buildRequest: NonNullable<GuidesHost['buildRequest']> = {
  Button: (p: BuildRequestButtonProps) => (
    <BuildRequestButton {...(p as unknown as Parameters<typeof BuildRequestButton>[0])} />
  ),
};

/** Cortex's product tour of the open project branch, offered under "Cortex tutorials". */
const cortexTours = (where: Where | null): BuiltInTours => ({
  key: 'cortex',
  label: 'Cortex tutorials',
  empty: 'Open a project to take the product tour.',
  tours: () => (where ? [coreTour(where)] : []),
});

/** Cortex's affordances for the guides views and the tour overlay, for one project branch (none: no project open). */
export function CortexGuidesHost({
  where,
  Provider = GuidesHostProvider,
  children,
}: {
  where: Where | null;
  Provider?: typeof GuidesHostProvider;
  children: React.ReactNode;
}) {
  const navigate = useNavigate();
  const slug = where?.slug ?? '';
  const branch = where?.branch ?? DEFAULT_BRANCH;
  const host = React.useMemo<GuidesHost>(
    () => ({
      client: slug ? guidesClientFor({ slug, branch }) : null,
      scope: `${slug}/${branch}`,
      useSearchParams,
      navigate: (to) => void navigate(to),
      appPath: (app) => projectPath(`apps/${encodeURIComponent(app)}`, slug, branch),
      routeOf: (path) => fillProjectPath(path, { slug, branch }),
      buildRequest,
    }),
    [navigate, slug, branch],
  );
  return <Provider host={host}>{children}</Provider>;
}

/** /guides — the SDK's Guides page of the routed project branch. */
export function GuidesPage() {
  const { slug = '', branch = DEFAULT_BRANCH } = useParams();
  return (
    <OwnViews
      module="guides"
      slug={slug}
      branch={branch}
      cortex={{ GuidesHostProvider, GuidesPage: SdkGuidesPage }}
    >
      {(v, key) => (
        <CortexGuidesHost key={key} where={{ slug, branch }} Provider={v.GuidesHostProvider}>
          <v.GuidesPage />
        </CortexGuidesHost>
      )}
    </OwnViews>
  );
}

/**
 * The shell's tour overlay (the Tutorial button, the speed dial's Guides, every launchTour()). The
 * product tour walks the cortex shell: a project's standalone shell has none of it, so there only the
 * project's own guides are offered.
 */
export function CortexTourHost({ slug, branch }: { slug: string | null; branch: string | null }) {
  const where = slug ? { slug, branch: branch ?? DEFAULT_BRANCH } : null;
  return (
    // Not remounted per project: a running tour survives the route changes it makes.
    <CortexGuidesHost where={where}>
      <TourHost startEvent={START_TUTORIAL_EVENT} builtIn={cortexTours(where)} />
    </CortexGuidesHost>
  );
}
